import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { getAuth } from 'firebase/auth';
import { triggerAssessmentCompleteNotification } from './notificationTriggerService';
import { generateAndFileAssessmentPdf } from './assessmentPdfService';

/**
 * Resolving the user -> lead account link.
 *
 * This used to search accounts for a guessed spelling of the user's email, which
 * silently stranded assessments whenever the account stored different
 * capitalisation than auth did (e.g. account "Byron@LDBpartners.com" vs auth
 * "byron@ldbpartners.com" — the guesses only ever capitalised the local part,
 * never the domain). Three real accounts lost their assessment that way.
 *
 * `accountId` / `clientId` on the user doc is the actual foreign key and is set
 * at signup, so it comes first. Email is a fallback for older user docs only.
 *
 * A server-side trigger (functions/syncAssessmentToAccount.js) mirrors the same
 * blocks independently, so a miss here is a delay, never a loss.
 */
const SYNC_FAILURES_COLLECTION = 'assessmentSyncFailures';

/** Read the signed-in user's own doc for the account link, if the payload lacks it. */
const accountIdFromUserDoc = async () => {
  try {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return null;
    const snap = await getDoc(doc(db, 'users', uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    return data.accountId || data.clientId || null;
  } catch (error) {
    console.error('Error reading account link from user doc:', error);
    return null;
  }
};

/**
 * @returns {Promise<{leadId: string|null, via: string}>}
 */
export const resolveLeadAccountId = async (userEmail, userData = {}) => {
  const linked = userData.accountId || userData.clientId || (await accountIdFromUserDoc());
  if (linked) {
    const snap = await getDoc(doc(db, 'accounts', String(linked)));
    if (snap.exists()) return { leadId: snap.id, via: 'accountId' };
    console.warn('Account link points at a missing account:', linked);
  }

  const email = String(userEmail || '').trim();
  if (!email) return { leadId: null, via: 'no-email' };

  // Firestore equality is case-sensitive and has no lowercase operator, so we can
  // only try the spellings we hold. Anything past this is the server sweep's job.
  const variants = [...new Set([email, email.toLowerCase()])];
  for (const value of variants) {
    for (const field of ['contact.email', 'email']) {
      // Portal logins may only read their own account (firestore.rules), so these
      // email searches only succeed for staff; a refusal just means "not found".
      const found = await getDocs(query(collection(db, 'accounts'), where(field, '==', value))).catch(() => null);
      if (found && !found.empty) return { leadId: found.docs[0].id, via: `email:${field}` };
    }
  }

  return { leadId: null, via: 'no-match' };
};

/**
 * Leave a breadcrumb when the copy can't find its account.
 *
 * The previous behaviour was a console.log the client never saw and nobody
 * monitored, which is why a stranded assessment could sit unnoticed for weeks.
 */
const recordSyncFailure = async (userEmail, reason) => {
  try {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    await setDoc(
      doc(db, SYNC_FAILURES_COLLECTION, uid),
      {
        uid,
        email: userEmail || null,
        reason,
        source: 'client',
        resolved: false,
        lastAttemptAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Could not record assessment sync failure:', error);
  }
};

/**
 * Copy business assessment data from user document to lead document
 * @param {string} userEmail - The user's email address
 * @param {Object} businessAssessmentData - The business assessment data to copy
 * @param {Object} businessAssessmentProgress - The business assessment progress data to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyBusinessAssessmentToLead = async (userEmail, businessAssessmentData, businessAssessmentProgress) => {
  try {
    const { leadId, via } = await resolveLeadAccountId(userEmail);

    if (!leadId) {
      console.warn('No lead account found for user:', userEmail, `(${via})`);
      await recordSyncFailure(userEmail, via);
      return false;
    }
    console.log(`Resolved lead account ${leadId} via ${via}`);

    // Prepare the data to copy
    const updateData = {
      lastUpdated: new Date().toISOString()
    };

    // Add business assessment data if it exists
    if (businessAssessmentData) {
      updateData.businessAssessment = businessAssessmentData;
    }

    // Add business assessment progress data if it exists
    if (businessAssessmentProgress) {
      updateData.businessAssessmentProgress = businessAssessmentProgress;
    }

    // Update the lead document
    await updateDoc(doc(db, 'accounts', leadId), updateData);
    
    return true;
  } catch (error) {
    console.error('Error copying business assessment to lead:', error);
    return false;
  }
};

/**
 * Copy all assessment data from user document to lead document
 * @param {string} userEmail - The user's email address
 * @param {Object} userData - The complete user data object
 * @returns {Promise<boolean>} Success status
 */
export const copyAllAssessmentDataToLead = async (userEmail, userData) => {
  try {
    console.log('=== LEAD ASSESSMENT SERVICE - RECEIVED DATA ===');
    console.log('User Email:', userEmail);
    console.log('Business Assessment Data:', userData.businessAssessment);
    console.log('Sales Data:', userData.salesData);
    console.log('Operations Data:', userData.operationsData);
    console.log('Finance Data:', userData.financeData);
    console.log('Marketing Data:', userData.marketingData);
    console.log('Tech Data:', userData.techData);

    // If userData doesn't contain complete sales data, try to read from Firestore
    if (userData.salesData && (!userData.salesData.twoYearsAgo && !userData.salesData.oneYearAgo && !userData.salesData.currentYearProjection)) {
      try {
        // Find user document by email
        const usersQuery = query(
          collection(db, 'users'),
          where('email', '==', userEmail)
        );
        const usersSnapshot = await getDocs(usersQuery);
        
        if (!usersSnapshot.empty) {
          const userDoc = usersSnapshot.docs[0];
          const userDocData = userDoc.data();
          
          // Merge Firestore sales data with passed userData
          if (userDocData.salesData) {
            userData.salesData = { ...userData.salesData, ...userDocData.salesData };
          }
        }
      } catch (firestoreError) {
        console.error('Error reading user document from Firestore:', firestoreError);
      }
    }

    const updateData = {
      lastUpdated: new Date().toISOString()
    };

    // Copy discovery data if it exists
    if (userData.discovery) {
      updateData.discovery = userData.discovery;
      updateData.discoveryCompleted = true;
      updateData.discoveryCompletedAt = userData.discovery.discoveryCompletedAt || new Date().toISOString();
    }

    // Copy business assessment data if it exists
    if (userData.businessAssessment) {
      updateData.businessAssessment = userData.businessAssessment;
    }

    // Copy business assessment progress data if it exists
    if (userData.businessAssessmentProgress) {
      updateData.businessAssessmentProgress = userData.businessAssessmentProgress;
    }

    // Copy other assessment data if it exists
    if (userData.salesData) {
      // Check if this is complete sales data (has other fields like salesRating, goalsData, etc.)
      const hasCompleteSalesData = userData.salesData.salesRating !== undefined || 
                                   userData.salesData.goalsData !== undefined || 
                                   userData.salesData.referralBusinessPercentage !== undefined;
      
      if (hasCompleteSalesData) {
        // Full sales data replacement - this is complete sales data from ExistingConditions
        updateData.salesData = userData.salesData;
      } else if (userData.salesData.twoYearsAgo !== undefined || userData.salesData.oneYearAgo !== undefined || userData.salesData.currentYearProjection !== undefined) {
        // Use dot notation to update only the revenue fields - this is revenue-only data from SalesRevenue component
        if (userData.salesData.twoYearsAgo !== undefined) {
          updateData['salesData.twoYearsAgo'] = userData.salesData.twoYearsAgo;
        }
        if (userData.salesData.oneYearAgo !== undefined) {
          updateData['salesData.oneYearAgo'] = userData.salesData.oneYearAgo;
        }
        if (userData.salesData.currentYearProjection !== undefined) {
          updateData['salesData.currentYearProjection'] = userData.salesData.currentYearProjection;
        }
        if (userData.salesData.chartData !== undefined) {
          updateData['salesData.chartData'] = userData.salesData.chartData;
        }
      } else {
        // Fallback - full sales data replacement
        updateData.salesData = userData.salesData;
      }
    }

    // Note: Revenue data is automatically included when copying the entire salesData object above

    // Map marketing data to lead structure
    if (userData.marketingData) {
      updateData.marketingData = userData.marketingData;
    }

    // Map operations data to lead structure
    if (userData.operationsData) {
      updateData.operationsData = userData.operationsData;
    }

    // Map finance data to lead structure
    if (userData.financeData) {
      updateData.financeData = userData.financeData;
    }

    // Map tech data to lead structure
    if (userData.techData) {
      updateData.techData = userData.techData;
    }

    // Resolve the account by its real foreign key, not by guessing email spellings.
    const { leadId, via } = await resolveLeadAccountId(userEmail, userData);

    if (!leadId) {
      console.warn('=== NO LEAD ACCOUNT FOUND ===', { userEmail, via });
      await recordSyncFailure(userEmail, via);
      return false;
    }

    const leadDoc = await getDoc(doc(db, 'accounts', leadId));

    console.log('=== LEAD ASSESSMENT SERVICE - SENDING TO LEAD DOCUMENT ===');
    console.log('Resolved via:', via);
    console.log('Lead ID:', leadId);
    console.log('Update Data Keys:', Object.keys(updateData));
    console.log('Business Assessment in Update Data:', updateData.businessAssessment);
    console.log('Sales Data in Update Data:', updateData.salesData);
    console.log('Operations Data in Update Data:', updateData.operationsData);
    console.log('Finance Data in Update Data:', updateData.financeData);
    console.log('Marketing Data in Update Data:', updateData.marketingData);
    console.log('Tech Data in Update Data:', updateData.techData);
    
    // Debug specific fields that were missing
    if (updateData.financeData) {
      console.log('Finance Data - monthlyOverhead:', updateData.financeData.monthlyOverhead);
      console.log('Finance Data - npmData:', updateData.financeData.npmData);
    }
    if (updateData.operationsData) {
      console.log('Operations Data - gpmData:', updateData.operationsData.gpmData);
      console.log('Operations Data - orgChartData:', updateData.operationsData.orgChartData);
    }
    if (updateData.marketingData) {
      console.log('Marketing Data - leadsData:', updateData.marketingData.leadsData);
    }
    if (updateData.techData) {
      console.log('Tech Data - techStackData:', updateData.techData.techStackData);
    }
    if (updateData.salesData) {
      console.log('Sales Data - oneYearAgo:', updateData.salesData.oneYearAgo);
      console.log('Sales Data - currentYearProjection:', updateData.salesData.currentYearProjection);
      console.log('Sales Data - nextYearGoal:', updateData.salesData.nextYearGoal);
    }

    // Capture lead data before update for comparison
    const leadDataBefore = leadDoc.data() || {};

    // Determine if this save represents the moment the assessment was completed
    const previousProgress = leadDataBefore.businessAssessmentProgress?.progress;
    const newProgress = userData.businessAssessmentProgress?.progress;
    const hasNewProgressValue = typeof newProgress === 'number';
    const assessmentIsComplete = hasNewProgressValue && newProgress >= 99;
    const assessmentJustCompleted =
      assessmentIsComplete && (typeof previousProgress !== 'number' || previousProgress < 99);

    // If lead is in Assessment stage and assessment just completed, set substatus to Assessment Done
    // and substatusDates (same as NDA flow)
    const completedDate = new Date().toISOString();
    if (leadDataBefore.stage === 'Assessment' && assessmentJustCompleted) {
      updateData.substatus = 'Assessment Done';
      if (!leadDataBefore.substatusDates?.['Assessment Done']) {
        updateData.substatusDates = { ...(leadDataBefore.substatusDates || {}), 'Assessment Done': completedDate };
      }
    }

    // Update the lead document
    console.log('=== ATTEMPTING TO UPDATE LEAD DOCUMENT ===');
    console.log('Lead ID:', leadId);
    console.log('Update Data:', JSON.stringify(updateData, null, 2));
    
    await updateDoc(doc(db, 'accounts', leadId), updateData);
    
    console.log('Successfully updated lead document with all assessment data');
    
    // Trigger Assessment Complete notification as soon as the assessment is actually completed
    // (same timing behavior as NDA signed notifications).
    if (assessmentJustCompleted) {
      const notificationCompletedDate =
        updateData.substatusDates?.['Assessment Done'] ||
        leadDataBefore.substatusDates?.['Assessment Done'] ||
        completedDate;

      try {
        await triggerAssessmentCompleteNotification({
          ...leadDataBefore,
          leadId,
          id: leadId,
          completedDate: notificationCompletedDate,
          substatus: 'Assessment Done',
          timestamp: notificationCompletedDate
        });
        console.log('✅ Assessment Complete notification sent successfully');
      } catch (notificationError) {
        console.error('Error sending assessment complete notification:', notificationError);
      }
    }
    
    // Verify the data was saved by reading it back
    const verifyDoc = await getDoc(doc(db, 'accounts', leadId));
    if (verifyDoc.exists()) {
      const savedData = verifyDoc.data();
      console.log('=== VERIFICATION - DATA SAVED TO LEAD ===');
      console.log('Business Assessment:', savedData.businessAssessment ? 'EXISTS' : 'MISSING');
      console.log('Sales Data:', savedData.salesData ? 'EXISTS' : 'MISSING');
      console.log('Operations Data:', savedData.operationsData ? 'EXISTS' : 'MISSING');
      console.log('Finance Data:', savedData.financeData ? 'EXISTS' : 'MISSING');
      console.log('Marketing Data:', savedData.marketingData ? 'EXISTS' : 'MISSING');
      console.log('Tech Data:', savedData.techData ? 'EXISTS' : 'MISSING');

      // File a PDF of the completed assessment in the account's document collection
      // (General / internal only). Reads the account back post-update so the PDF
      // matches what was actually saved; a failure here never fails the save.
      if (assessmentIsComplete) {
        const filed = await generateAndFileAssessmentPdf({ accountId: leadId, account: savedData });
        console.log(filed ? '✅ Assessment PDF filed in documents' : '⚠️ Assessment PDF was not filed');
      }
    }

    return true;
  } catch (error) {
    console.error('Error copying all assessment data to lead:', error);
    console.error('Error details:', error.message);
    console.error('Error stack:', error.stack);
    return false;
  }
};

/**
 * Get lead assessment data by lead ID
 * @param {string} leadId - The lead's document ID
 * @returns {Promise<Object|null>} Lead assessment data or null if not found
 */
export const getLeadAssessmentData = async (leadId) => {
  try {
    const leadDoc = await getDoc(doc(db, 'accounts', leadId));
    
    if (leadDoc.exists()) {
      const leadData = leadDoc.data();
      return {
        discovery: leadData.discovery || null,
        businessAssessment: leadData.businessAssessment || null,
        businessAssessmentProgress: leadData.businessAssessmentProgress || null,
        salesData: leadData.salesData || null,
        marketingData: leadData.marketingData || null,
        operationsData: leadData.operationsData || null,
        financeData: leadData.financeData || null,
        techData: leadData.techData || null,
        hrData: leadData.hrData || null
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting lead assessment data:', error);
    return null;
  }
};
